import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("auth_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const persist = (token, u) => {
    localStorage.setItem("auth_token", token);
    localStorage.setItem("auth_user", JSON.stringify(u));
    setUser(u);
  };

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      persist(data.token, data.user);
      // Refresh from /auth/me to ensure we have the latest server-side flags
      // (e.g. is_admin) even if the login payload predates a schema change.
      try {
        const me = await api.get("/auth/me");
        localStorage.setItem("auth_user", JSON.stringify(me.data));
        setUser(me.data);
        return me.data;
      } catch {
        return data.user;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (email, password, full_name) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, full_name });
      persist(data.token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem("auth_user", JSON.stringify(data));
      setUser(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("auth_token") && !user) {
      refresh();
    }
  }, [user, refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
